import { useCallback, useMemo } from 'react'
import type { AuditSubjectGroup } from '@lms/shared-types'
import { listAuditSubjectGroups } from '../../../api/audit'
import { FadeIn } from '../../primitives/FadeIn'
import { ThinkingDots } from '../../primitives/ThinkingDots'
import { PaginationControls } from '../../lists/PaginationControls'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../../../lib/use-paged-list'
import { AuditGroupRow } from './AuditGroupRow'

export interface AuditGroupedListProps {
  category?: string | undefined
  q?: string | undefined
  subjectType?: string | undefined
  source?: string | undefined
}

/**
 * Grouped-by-object audit view: one collapsible entry per object, newest
 * change first, each expandable to its full event history. Owns its own
 * paginated query so it only fetches while mounted (the timeline view uses a
 * separate hook).
 */
export function AuditGroupedList({
  category,
  q,
  subjectType,
  source,
}: AuditGroupedListProps): JSX.Element {
  const filterArgs = useMemo(
    () => ({
      ...(category ? { category } : {}),
      ...(q ? { q } : {}),
      ...(subjectType ? { subjectType } : {}),
      ...(source ? { source } : {}),
    }),
    [category, q, subjectType, source],
  )

  const pagedFetchGroups = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<AuditSubjectGroup>> => {
      const result = await listAuditSubjectGroups({
        ...filterArgs,
        page,
        limit: pageSize,
      })
      return { rows: result.data, total: result.meta.total }
    },
    [filterArgs],
  )

  const pagedList = usePagedList<AuditSubjectGroup>({
    queryKey: ['audit-subjects', filterArgs],
    fetcher: pagedFetchGroups,
  })

  const rows = pagedList.rows
  const isLoading = pagedList.isLoading
  const isError = pagedList.isError

  if (isLoading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <ThinkingDots label="Loading audit" />
      </div>
    )
  }
  if (isError) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger-fg)' }}>
        Could not load the audit log.
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <FadeIn>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
          No audit entries match the current filters.
        </div>
      </FadeIn>
    )
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table
          data-testid="audit-groups"
          style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}
        >
          <thead>
            <tr>
              <th style={thStyle} aria-label="Expand" />
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Entity</th>
              <th style={thStyle}>Latest event</th>
              <th style={thStyle}>Last changed by</th>
              <th style={thStyle}>When</th>
            </tr>
          </thead>
          {rows.map((group) => (
            <AuditGroupRow key={`${group.subjectType}:${group.subjectId ?? 'none'}`} group={group} />
          ))}
        </table>
      </div>
      <PaginationControls
        page={pagedList.page}
        pageSize={pagedList.pageSize}
        total={pagedList.total}
        totalPages={pagedList.totalPages}
        pageSizeOptions={pagedList.pageSizeOptions}
        rangeStart={pagedList.rangeStart}
        rangeEnd={pagedList.rangeEnd}
        canPrev={pagedList.canPrev}
        canNext={pagedList.canNext}
        onPageChange={pagedList.setPage}
        onPageSizeChange={pagedList.setPageSize}
        isFetching={pagedList.isFetching}
      />
    </>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--text-3)',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--french-gray-2)',
  whiteSpace: 'nowrap',
}
